import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleAnalytics } from './components/SEO';
import axios from 'axios';

// Contexts
import { CurrencyProvider } from './contexts/CurrencyContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { WishlistProvider } from './contexts/WishlistContext';

// Components
import TawkToChat from './components/TawkToChat';
import ScrollToTop from './components/ScrollToTop';
import CallTracker from './components/CallTracker';
import StickyCallButton from './components/StickyCallButton';
import { TopBarBanner } from './components/CampaignBanner';
import { NewsletterPopup } from './components/Newsletter';

// Pages
import Home from './pages/Home';
import Products from './pages/Products';
import ProductLine from './pages/ProductLine';
import ResourceGateway from './pages/ResourceGateway';
import IndustryPage from './pages/IndustryPage';
import CapacityCalculator from './pages/CapacityCalculator';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import CheckoutSuccess from './pages/CheckoutSuccess';
import Contact from './pages/Contact';
import Financing from './pages/Financing';
import TrackOrder from './pages/TrackOrder';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import PageBuilder from './pages/PageBuilder';
import DynamicPage from './pages/DynamicPage';
import NotFound from './pages/NotFound';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CompareProducts from './pages/CompareProducts';
import Recipes from './pages/Recipes';
import RecipeDetail from './pages/RecipeDetail';
import ComingSoon from './pages/ComingSoon';
import Lifestyle from './pages/Lifestyle';
import Commercial from './pages/Commercial';
import TechnicalSupport from './pages/TechnicalSupport';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Warranty from './pages/Warranty';
import CancellationPolicy from './pages/CancellationPolicy';
import ReturnPolicy from './pages/ReturnPolicy';
import Accessibility from './pages/Accessibility';
import CustomerSupport from './pages/CustomerSupport';
import AffiliateProgram from './pages/AffiliateProgram';
import ReferAFriend from './pages/ReferAFriend';
import Wholesale from './pages/Wholesale';
import RepairServices from './pages/RepairServices';
import RepairQuote from './pages/RepairQuote';
import ApproveQuote from './pages/ApproveQuote';
import MachineLanding from './pages/MachineLanding';
import LocationLanding from './pages/LocationLanding';
import Guides from './pages/Guides';
import GuideArticle from './pages/GuideArticle';
import ProcessRates from './pages/ProcessRates';
import Documents from './pages/Documents';
import FreezeDryAcademy from './pages/FreezeDryAcademy';
import Resources from './pages/Resources';
import Support from './pages/Support';
import Configurator from './pages/Configurator';
import Wishlist from './pages/Wishlist';
import SearchResults from './pages/SearchResults';
import AboutUs from './pages/AboutUs';
import FAQ from './pages/FAQ';
import VideoGallery from './pages/VideoGallery';
import Unsubscribe from './pages/Unsubscribe';
import MarketingLandingPage from './pages/MarketingLandingPage';
import ReportBug from './pages/ReportBug';
import BugStatus from './pages/BugStatus';
import BugSatisfaction from './pages/BugSatisfaction';
import AcceptInvite from './pages/AcceptInvite';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Site launch status - set to true when ready to go live
const SITE_LAUNCHED = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Cart Context
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const res = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(res.data);
        } catch {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    localStorage.setItem('token', res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/register`, { name, email, password });
    localStorage.setItem('token', res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Provider
const CartProvider = ({ children }) => {
  // Initialize from localStorage
  const getInitialItems = () => {
    try {
      const saved = localStorage.getItem('cart');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('[CartProvider] Error loading cart:', e);
      return [];
    }
  };
  
  const [items, setItemsState] = useState(getInitialItems);
  
  // Custom setItems that always syncs to localStorage
  const setItems = (newItemsOrUpdater) => {
    setItemsState(prevItems => {
      const newItems = typeof newItemsOrUpdater === 'function' 
        ? newItemsOrUpdater(prevItems) 
        : newItemsOrUpdater;
      // Immediately sync to localStorage
      localStorage.setItem('cart', JSON.stringify(newItems));
      return newItems;
    });
  };

  // On mount, ensure state matches localStorage (handles remount scenarios)
  useEffect(() => {
    const savedItems = getInitialItems();
    const currentItemsStr = JSON.stringify(items);
    const savedItemsStr = JSON.stringify(savedItems);
    
    // If localStorage has items but state is empty, restore from localStorage
    if (savedItems.length > 0 && items.length === 0 && currentItemsStr !== savedItemsStr) {
      console.log('[CartProvider] Restoring cart from localStorage:', savedItems.length, 'items');
      setItemsState(savedItems);
    }
  }, []); // Only run on mount

  // Generate unique cart item ID based on product and selected add-ons
  const generateCartItemId = (productId, selectedAddons = []) => {
    if (!selectedAddons || selectedAddons.length === 0) {
      return productId;
    }
    // Sort addon IDs for consistent hashing
    const addonKey = selectedAddons
      .map(a => `${a.id}:${a.quantity || 1}`)
      .sort()
      .join('|');
    return `${productId}__${addonKey}`;
  };

  const addItem = (product, quantity = 1, options = {}) => {
    const { selectedAddons = [], currency = 'USD' } = options;
    
    // Calculate add-ons total
    const addonsTotal = (selectedAddons || []).reduce((sum, addon) => {
      const addonQty = addon.quantity || 1;
      const addonPrice = currency === 'CAD' ? (addon.price_cad || addon.price) : addon.price;
      return sum + (addonPrice * addonQty);
    }, 0);
    
    const cartItemId = generateCartItemId(product.id, selectedAddons);
    const basePrice = currency === 'CAD' ? (product.price_cad || product.price) : product.price;
    
    setItems(prev => {
      // Check for exact match (same product + same addons configuration)
      const existing = prev.find(i => i.cart_item_id === cartItemId);
      
      if (existing) {
        return prev.map(i => 
          i.cart_item_id === cartItemId 
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      
      return [...prev, {
        cart_item_id: cartItemId,
        product_id: product.id,
        name: product.name,
        price: basePrice,
        raw_price: product.price,
        addons_price: addonsTotal,
        total_price: basePrice + addonsTotal,
        image_url: product.image_url,
        region_prices: product.region_prices || null,
        regional_sale_prices: product.regional_sale_prices || null,
        on_sale: product.on_sale || false,
        sale_type: product.sale_type,
        sale_value: product.sale_value,
        sale_price: product.sale_price,
        sale_start_date: product.sale_start_date,
        sale_end_date: product.sale_end_date,
        quantity,
        selectedAddons: selectedAddons || [],
        currency
      }];
    });
  };

  const removeItem = (cartItemId) => {
    setItems(prev => prev.filter(i => (i.cart_item_id || i.product_id) !== cartItemId));
  };

  const updateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeItem(cartItemId);
      return;
    }
    setItems(prev => prev.map(i => 
      (i.cart_item_id || i.product_id) === cartItemId ? { ...i, quantity } : i
    ));
  };

  const clearCart = () => setItems([]);

  // Calculate total including add-ons
  const total = items.reduce((sum, i) => {
    const itemTotal = (i.total_price || i.price) * i.quantity;
    return sum + itemTotal;
  }, 0);
  
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, total, itemCount }}>
      {children}
    </CartContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3A5A40] border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Admin Preview Route Guard - requires login to access site before launch
const PreviewRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3A5A40] border-t-transparent"></div>
      </div>
    );
  }
  
  // If site not launched and user not logged in, redirect to coming soon
  if (!SITE_LAUNCHED && !user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function IndustryRoute() {
  const { slug } = useParams();
  return <IndustryPage slug={slug} />;
}

function App() {
  return (
    <HelmetProvider>
    <SettingsProvider>
    <CurrencyProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
          <BrowserRouter>
            <ScrollToTop />
            <CallTracker />
            <StickyCallButton />
            <GoogleAnalytics />
            {SITE_LAUNCHED && <TopBarBanner />}
            <div className="min-h-screen bg-[#F5F5F0]">
              <Routes>
                {/* Coming Soon - Default route when site not launched */}
                {!SITE_LAUNCHED && (
                  <Route path="/" element={<ComingSoon />} />
                )}
                
                {/* Admin Preview Routes - Requires login before launch */}
                <Route path="/preview" element={
                  <PreviewRoute><Home /></PreviewRoute>
                } />
                <Route path="/preview/products" element={
                  <PreviewRoute><Products /></PreviewRoute>
                } />
                <Route path="/preview/products/:id" element={
                  <PreviewRoute><ProductDetail /></PreviewRoute>
                } />
                <Route path="/preview/cart" element={
                  <PreviewRoute><Cart /></PreviewRoute>
                } />
                <Route path="/preview/contact" element={
                  <PreviewRoute><Contact /></PreviewRoute>
                } />
                <Route path="/preview/financing" element={
                  <PreviewRoute><Financing /></PreviewRoute>
                } />
                <Route path="/preview/blog" element={
                  <PreviewRoute><Blog /></PreviewRoute>
                } />
                <Route path="/preview/blog/:slug" element={
                  <PreviewRoute><BlogPost /></PreviewRoute>
                } />
                <Route path="/preview/recipes" element={
                  <PreviewRoute><Recipes /></PreviewRoute>
                } />
                <Route path="/preview/recipes/:slug" element={
                  <PreviewRoute><RecipeDetail /></PreviewRoute>
                } />
                <Route path="/preview/compare" element={
                  <PreviewRoute><CompareProducts /></PreviewRoute>
                } />
                <Route path="/preview/lifestyle/:slug" element={
                  <PreviewRoute><Lifestyle /></PreviewRoute>
                } />

                {/* Public Routes - Only accessible when site launched */}
                {SITE_LAUNCHED && (
                  <>
                    <Route path="/" element={<Home />} />
                    <Route path="/process-rates" element={<ProcessRates />} />
                    <Route path="/technical-support" element={<TechnicalSupport />} />
                    <Route path="/diagnostic-quote" element={<RepairQuote />} />
                    <Route path="/products" element={<ProcessRates />} />
                    <Route path="/catalog" element={<Navigate to="/process-rates" replace />} />
                    <Route path="/entrepreneur" element={<ProductLine segment="entrepreneur" />} />
                    <Route path="/commercial-scientific" element={<ProductLine segment="commercial-scientific" />} />
                    <Route path="/home-solutions" element={<ResourceGateway slug="home-solutions" />} />
                    <Route path="/repair-service" element={<ResourceGateway slug="repair-service" />} />
                    <Route path="/industries/:slug" element={<IndustryRoute />} />
                    <Route path="/capacity-calculator" element={<CapacityCalculator />} />
                    <Route path="/products/:id" element={<Navigate to="/process-rates" replace />} />
                    <Route path="/cart" element={<Navigate to="/" replace />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/financing" element={<Financing />} />
                    <Route path="/track-order" element={<TrackOrder />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/compare" element={<CompareProducts />} />
                    <Route path="/recipes" element={<Recipes />} />
                    <Route path="/recipes/:slug" element={<RecipeDetail />} />
                    <Route path="/lifestyle/:slug" element={<Lifestyle />} />
                    <Route path="/commercial" element={<Commercial />} />
                    <Route path="/business-solutions" element={<Commercial />} />
                    
                    {/* Legal Pages */}
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-use" element={<TermsOfUse />} />
                    <Route path="/warranty" element={<Warranty />} />
                    <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                    <Route path="/return-policy" element={<ReturnPolicy />} />
                    <Route path="/accessibility" element={<Accessibility />} />
                    
                    {/* Support & Programs */}
                    <Route path="/customer-support" element={<CustomerSupport />} />
                    <Route path="/repair" element={<RepairServices />} />
                    <Route path="/repair/quote" element={<RepairQuote />} />
                    <Route path="/machines/:slug" element={<MachineLanding />} />
                    <Route path="/freeze-dryer-repair/:region" element={<LocationLanding />} />
                    <Route path="/guides" element={<Guides />} />
                    <Route path="/guides/:slug" element={<GuideArticle />} />
                    <Route path="/affiliate-program" element={<AffiliateProgram />} />
                    <Route path="/refer-a-friend" element={<ReferAFriend />} />
                    <Route path="/wholesale" element={<Wholesale />} />
                    
                    {/* Resources */}
                    <Route path="/documents" element={<Documents />} />
                    <Route path="/freeze-dry-academy" element={<FreezeDryAcademy />} />
                    <Route path="/resources" element={<Resources />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/configurator" element={<Configurator />} />
                    <Route path="/wishlist" element={<Wishlist />} />
                    <Route path="/search" element={<SearchResults />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/resources/faq" element={<FAQ />} />
                    <Route path="/resources/videos" element={<VideoGallery />} />
                    <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
                    <Route path="/coming-soon" element={<ComingSoon />} />
                    
                    {/* Bug Tracker */}
                    <Route path="/report-bug" element={<ReportBug />} />
                    <Route path="/bug-status/:bugId" element={<BugStatus />} />
                    <Route path="/bug-satisfaction/:bugId" element={<BugSatisfaction />} />
                    
                    {/* Team Invite */}
                    <Route path="/accept-invite" element={<AcceptInvite />} />
                    
                    {/* Marketing Landing Pages */}
                    <Route path="/promo/:slug" element={<MarketingLandingPage />} />
                  </>
                )}
                
                {/* Legacy e-commerce checkout removed for the rebuild lead-gen site */}
                <Route path="/checkout/success" element={<Navigate to="/" replace />} />
                <Route path="/approve-quote" element={<ApproveQuote />} />
                <Route path="/track-order" element={<TrackOrder />} />
              
              {/* Admin Routes - Always accessible */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/pages/builder" element={
                <ProtectedRoute>
                  <PageBuilder />
                </ProtectedRoute>
              } />
              <Route path="/admin/pages/builder/:pageId" element={
                <ProtectedRoute>
                  <PageBuilder />
                </ProtectedRoute>
              } />
              
              {/* Dynamic Pages */}
              <Route path="/page/:slug" element={<DynamicPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster position="bottom-right" richColors />
            <TawkToChat />
            {SITE_LAUNCHED && <NewsletterPopup />}
          </div>
          </BrowserRouter>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </CurrencyProvider>
    </SettingsProvider>
    </HelmetProvider>
  );
}

export default App;
